import { AfterViewInit, Component, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Md5 } from 'ts-md5/dist/md5';
import { getServerConfig } from './origami-api/server-config';
import { NavigationCancel, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { AppLoadingService, NotificationsService } from '@app/services';
import { AppService } from '@app/app.service';
import { AllCountriesService } from '@app/services/all-countries.service';
import { filter, take } from 'rxjs/operators';
import { AuthenticateService } from '@app/origami-api/services/authenticate/authenticate.service';
import { SessionService } from '@app/session/session.service';
import { ErrorCode } from '@app/origami-api/constants/error-codes';
import { LocalStorageKeys } from '@app/services/local-storage.service';
import { ThemeColorService } from './services/theme-color.service';
import { GlobalEventService, OldUiMessages } from '@app/services/global-event.service';
import { BridgeServicesInitializerService } from '@app/services/bridge-services-initializer.service';

@Component({
  standalone: false,
  selector: 'ti-root',
  template: '<router-outlet></router-outlet>',
  styleUrls: ['./../assets/styles/main.sass'],
  encapsulation: ViewEncapsulation.None,
})
export class AppComponent implements OnInit, AfterViewInit {
  // @HostListener('window:dragover', ['$event']) onDragOver($event) {
  //   $event.preventDefault();
  // }

  // @HostListener('window:drop', ['$event']) onDrop($event) {
  //   $event.preventDefault();
  // }

  private isJustAppLoaded: boolean = true; //TRL-4511
  readonly localStorageKeys = LocalStorageKeys;
  readonly strings = tiStrings.app.pages.main.components.userProfileDetails.theme;

  private md5: Md5;

  /** Eagerly constructs all registered iframe-bridge feature services. */
  private readonly _bridgeInit = inject(BridgeServicesInitializerService);

  constructor(
    private readonly titleService: Title,
    private readonly notificationsService: NotificationsService,
    private readonly router: Router,
    private readonly loadingService: AppLoadingService,
    private readonly appService: AppService,
    private readonly allCountriesService: AllCountriesService,
    private readonly authenticateService: AuthenticateService,
    private readonly sessionService: SessionService,
    private themeColorService: ThemeColorService,
    private globalEventService: GlobalEventService,
  ) {
    this.titleService.setTitle(getServerConfig().companyName);

    this.md5 = new Md5();

    this.router.events
      .pipe(
        filter(
          (event) =>
            event instanceof NavigationStart || event instanceof NavigationEnd || event instanceof NavigationCancel,
        ),
        // debounceTime(500),
      )
      .subscribe((event) => {
        if (event instanceof NavigationStart) {
          this.loadingService.showLoading();
        } else if (event instanceof NavigationEnd || event instanceof NavigationCancel) {
          this.loadingService.hideLoading();
          if (event instanceof NavigationEnd) {
            if (this.isJustAppLoaded && this.sessionService.domain.settings) {
              this.isJustAppLoaded = false;
              this.showBanner();
            }
          }
        }
      });

    this.globalEventService.on(OldUiMessages.maintenanceBannerPreview).subscribe((banner: string) => {
      this.showBanner(banner);
    });

    this.authenticateService
      .getAuthInfo([ErrorCode.TEMPORARY_LOGIN])
      .pipe(take(1))
      .subscribe(() => {
        // need to do this request before the app starts
        this.allCountriesService
          .getAllCountries()
          .pipe(take(1))
          .subscribe((res) => {
            if (tiStrings.constants.countries && res.Result.length) {
              res.Result.forEach((c) => {
                tiStrings.constants.countries[c.Iso2] = c.Name;
              });
            }
          }); // let's init the cache and patch strings
      });
  }

  ngOnInit() {
    this.sessionService.setErrorDescription();

    this.appService.init();
    this.themeColorService.setThemeColor();
  }

  ngAfterViewInit(): void {
    /*
    if (getServerConfig().serverCfg.maintenanceIsScheduled) {
      const wasBannerShown = !!LocalStorage.get(AppService.MAINTENANCE_BANNER_SHOWN_KEY);
      if (wasBannerShown) {
        return;
      }
      this.http.get(OrigamiUrls.maintenanceScheduledPage, {
        responseType: 'text'
      }).subscribe((response: string) => {
        const parser = new DOMParser();
        this.notificationsService.showBanner(parser.parseFromString(response, 'text/html').body.innerHTML, 'maintenance');
        LocalStorage.set(AppService.MAINTENANCE_BANNER_SHOWN_KEY, '1');
      });
    }
    */
  }

  private showBanner(bannerContent?: string) {
    let content;

    if (bannerContent) {
      content = String(bannerContent)
        .replace(/&gt;/g, '>')
        .replace(/&lt;/g, '<')
        .replace(/&quot;/g, '"')
        .replace(/&amp;/g, '&');
    } else {
      content = this.sessionService.domain.settings.general.general.maintenanceBanner.get();
    }

    const html = `<div>${content}</div>`;
    const htmlHash = this.md5.appendStr(html).end();

    if (
      this.sessionService.domain.settings.general.general.enableMaintenanceBanner.get() &&
      content &&
      (!this.sessionService.user.preferences.getMuteMaintenanceBanner() ||
        this.sessionService.user.preferences.getPreviousMaintenanceBannerHash() !== htmlHash)
    ) {
      this.notificationsService.showBanner(html, 'maintenance');
    }
  }
}
